import { X, Plus, ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRef } from 'react';

/**
 * ImagePreview - Displays a horizontal row of image thumbnails
 * Used in both snippet sender (with pending images) and snippet display (with uploaded URLs)
 *
 * @param {Object} props
 * @param {Array} props.images - Array of image objects or URLs
 *   For pending: { id, previewUrl, name }
 *   For uploaded: string (URL)
 * @param {Function} props.onRemove - Called with image id/index when remove clicked
 * @param {Function} props.onAdd - Called when add button clicked (for edit mode)
 * @param {Function} props.onClick - Called with index when thumbnail clicked (for lightbox)
 * @param {boolean} props.editable - Show remove buttons and add button
 * @param {boolean} props.canAddMore - Show add button (only if editable)
 * @param {string} props.size - 'sm' (60px) | 'md' (80px) | 'lg' (100px)
 */
export default function ImagePreview({
  images = [],
  onRemove,
  onAdd,
  onClick,
  editable = false,
  canAddMore = true,
  size = 'md',
}) {
  const fileInputRef = useRef(null);

  if (images.length === 0 && !editable) {
    return null;
  }

  const sizeClasses = {
    sm: 'w-[60px] h-[60px]',
    md: 'w-[80px] h-[80px]',
    lg: 'w-[100px] h-[100px]',
  };

  const getImageUrl = (image) => {
    // Handle both pending images (object with previewUrl) and uploaded URLs (string)
    if (typeof image === 'string') {
      return image;
    }
    return image.previewUrl;
  };

  const getImageId = (image, index) => {
    if (typeof image === 'string') {
      return index;
    }
    return image.id;
  };

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      onAdd?.(e.target.files);
      // Reset input so same file can be selected again
      e.target.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto py-2 px-1 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
      {images.map((image, index) => (
        <div
          key={getImageId(image, index)}
          className={cn(
            'relative flex-shrink-0 rounded-lg overflow-hidden border border-border/50 bg-muted/30 group',
            sizeClasses[size],
            onClick && 'cursor-pointer hover:border-primary/50 transition-colors'
          )}
          onClick={() => onClick?.(index)}
        >
          <img
            src={getImageUrl(image)}
            alt={typeof image === 'string' ? `Image ${index + 1}` : image.name}
            className="w-full h-full object-cover"
          />

          {/* Remove button */}
          {editable && onRemove && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(getImageId(image, index));
              }}
              className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
              title="Remove image"
            >
              <X size={12} />
            </button>
          )}
        </div>
      ))}

      {/* Add button (edit mode) */}
      {editable && canAddMore && onAdd && (
        <>
          <button
            type="button"
            onClick={handleAddClick}
            className={cn(
              'flex-shrink-0 rounded-lg border-2 border-dashed border-border/60 bg-muted/20 flex items-center justify-center text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-muted/40 transition-all',
              sizeClasses[size]
            )}
            title="Add image"
          >
            <Plus size={20} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
        </>
      )}

      {/* Empty state with add button */}
      {editable && images.length === 0 && canAddMore && onAdd && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ImageIcon size={14} />
          <span>Add images</span>
        </div>
      )}
    </div>
  );
}
